import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Box,
  Grid,
  Paper,
  Typography,
  Button,
  Chip,
  CircularProgress,
  Alert,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  IconButton,
  Tooltip,
  Divider,

  Collapse,
  Checkbox
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Save as SaveIcon,
  Undo as UndoIcon,
  CheckCircle as ApproveIcon,
  Cancel as RejectIcon,
  PlayArrow as StartReviewIcon,
  Edit as EditIcon,
  OpenInNew as OpenInNewIcon,
  ExpandMore as ExpandMoreIcon2,
  ExpandLess as ExpandLessIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  CheckCircle as CheckCircleIcon,
  CircleOutlined as CircleOutlinedIcon
} from '@mui/icons-material';
// Zusätzliche Dialog-Icons
import { Dialog, DialogTitle, DialogContent, DialogActions } from '@mui/material';
import { Close as CloseIcon, Warning as WarningIcon } from '@mui/icons-material';
import { useParams, useNavigate, replace } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
// SECURITY FIX: DOMPurify für XSS-Schutz
import DOMPurify from 'dompurify';
import { useUserRole } from '../../hooks/useUserRole';
import { QSAnalysisService } from '../../services';
import type { QSAnalysisDetail as QSAnalysisDetailType, QSAnforderung, QSReviewRequest, QSChangeRequest } from '../../types';
import SectionWrapper from '../SectionWrapper';
import QSRequirementCard from './QSAnalysisDetail/components/QSRequirementCard';
import UniversalBadge from '../common/UniversalBadge';

// Einfache Debounce-Funktion ohne lodash
const debounce = (func: Function, delay: number) => {
  let timeoutId: NodeJS.Timeout;
  return (...args: any[]) => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => func.apply(null, args), delay);
  };
};

// Globales CSS für geänderte Felder - basiert auf korrektem MUI-Beispiel
const globalModifiedFieldStyles = `
  .modified-field-container .MuiOutlinedInput-root {
    background-color: #fff8f0 !important;
  }
  .modified-field-container .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline {
    border-color: #ff6b35 !important;
    border-width: 2px !important;
  }
  .modified-field-container .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline {
    border-color: #e55a2b !important;
    border-width: 2px !important;
  }
  .modified-field-container .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline {
    border-color: #ff6b35 !important;
    border-width: 2px !important;
  }
  .modified-field-container .MuiOutlinedInput-root .MuiInputLabel-outlined {
    color: #ff6b35 !important;
    font-weight: 600 !important;
    background-color: #fff8f0 !important;
    padding-left: 6px !important;
    padding-right: 6px !important;
  }
  .modified-field-container .MuiOutlinedInput-root .MuiInputLabel-outlined.Mui-focused {
    color: #ff6b35 !important;
    font-weight: 600 !important;
    background-color: #fff8f0 !important;
  }
  .modified-field-container .MuiInputLabel-root {
    color: #ff6b35 !important;
    font-weight: 600 !important;
    background-color: #fff8f0 !important;
    padding-left: 6px !important;
    padding-right: 6px !important;
  }
  .modified-field-container .MuiInputLabel-root.Mui-focused {
    color: #ff6b35 !important;
    background-color: #fff8f0 !important;
  }
`;

// Style-Element einmal hinzufügen
if (typeof window !== 'undefined' && !document.getElementById('modified-field-styles')) {
  const styleElement = document.createElement('style');
  styleElement.id = 'modified-field-styles';
  styleElement.textContent = globalModifiedFieldStyles;
  document.head.appendChild(styleElement);
}

// Lokales Notizfeld für bessere Performance
const LocalNotesField = React.memo<{
  label: string;
  initialValue: string;
  disabled: boolean;
  onLocalChange: (value: string) => void;
  placeholder?: string;
  inputProps?: any;
  sx?: any;
}>(({ label, initialValue, disabled, onLocalChange, placeholder, inputProps = { style: { fontSize: '0.875rem' } }, sx = { mb: 2 } }) => {
  const [localValue, setLocalValue] = useState(initialValue);

  // Update lokaler Wert wenn sich initialValue ändert
  useEffect(() => {
    setLocalValue(initialValue);
  }, [initialValue]);

  // Debounced Change für Parent-Update
  const debouncedChange = useCallback(
    debounce((value: string) => {
      onLocalChange(value);
    }, 300), // 300ms für Notizen
    [onLocalChange]
  );

  const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setLocalValue(newValue);
    debouncedChange(newValue);
  }, [debouncedChange]);

  return (
    <TextField
      fullWidth
      multiline
      minRows={3}
      label={label}
      placeholder={placeholder}
      value={localValue}
      disabled={disabled}
      onChange={handleChange}
      inputProps={inputProps}
      size="small"
      sx={{
        ...sx,
        '& .MuiInputBase-input': { overflow: 'hidden', resize: 'vertical' },
        '& .MuiInputBase-inputMultiline': { overflow: 'hidden', resize: 'vertical' }
      }}
    />
  );
});

// Komponente zum Hervorheben von XML-Tags
// SECURITY FIX: DOMPurify sanitization gegen XSS
const HighlightedText: React.FC<{ text: string }> = ({ text }) => {
  // SECURITY: Erst Text escapen, dann nur sichere Highlighting-Spans hinzufügen
  const escapeHtml = (str: string): string => {
    return str
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');
  };

  // Escape erst den Input
  let escapedText = escapeHtml(text);

  // Dann füge sichere Highlighting-Spans hinzu (nur für bereits escapte XML-Tags)
  let highlightedText = escapedText
    // HTML-Entities hervorheben: &lt;Vertragswerk&gt;, &lt;Dokument&gt;, etc.
    .replace(
      /(&lt;\/?(?!table|tr|td|th|thead|tbody)[^&]*&gt;)/g,
      '<span style="color: #d63384; background-color: #f8f9fa; padding: 2px 4px; border-radius: 3px; font-weight: bold; font-family: monospace; border: 1px solid #d63384;">$1</span>'
    );

  // SECURITY: DOMPurify als letzte Schutzschicht - nur sichere Tags erlauben
  const sanitizedHtml = DOMPurify.sanitize(highlightedText, {
    ALLOWED_TAGS: ['span'],
    ALLOWED_ATTR: ['style']
  });

  return <span dangerouslySetInnerHTML={{ __html: sanitizedHtml }} />;
};

// Optimierte Textbelege-Komponente
const TextbelegeEditor = React.memo<{
  anforderungNummer: number;
  originalTextbelege: any[];
  canEdit: boolean;
  onTextbelegeChange: (anforderungNummer: number, textbelege: any[]) => void;
  isFieldModified?: (anforderungNummer: number, belegIndex: number, field: string) => boolean;
}>(({ anforderungNummer, originalTextbelege, canEdit, onTextbelegeChange, isFieldModified }) => {
  const [localTextbelege, setLocalTextbelege] = useState<any[]>(originalTextbelege);

  // Update lokale Textbelege wenn sich originalTextbelege ändert
  useEffect(() => {
    setLocalTextbelege(originalTextbelege);
  }, [originalTextbelege]);

  // Debounced Change für Parent-Update
  const debouncedChange = useCallback(
    debounce((newTextbelege: any[]) => {
      onTextbelegeChange(anforderungNummer, newTextbelege);
    }, 300),
    [anforderungNummer, onTextbelegeChange]
  );

  // Textbelege-Feld ändern mit lokaler State-Optimierung
  const handleFieldChange = useCallback((belegIndex: number, field: string, value: string) => {
    const updatedTextbelege = [...localTextbelege];
    updatedTextbelege[belegIndex] = {
      ...updatedTextbelege[belegIndex],
      [field]: value
    };
    setLocalTextbelege(updatedTextbelege); // Sofort lokale UI-Update
    debouncedChange(updatedTextbelege); // Debounced Parent-Update
  }, [localTextbelege, debouncedChange]);

  // Textbelege löschen
  const handleDelete = useCallback((belegIndex: number) => {
    const newTextbelege = localTextbelege.filter((_, index) => index !== belegIndex);
    setLocalTextbelege(newTextbelege); // Sofort lokale UI-Update
    debouncedChange(newTextbelege); // Debounced Parent-Update
  }, [localTextbelege, debouncedChange]);

  // Neue Textbelege hinzufügen
  const handleAdd = useCallback(() => {
    const newTextbelege = [...localTextbelege, {
      belegtyp: "normal",
      dokumenttyp: "",
      quellenangabe: "",
      textauszug: "",
      relevanz: ""
    }];
    setLocalTextbelege(newTextbelege); // Sofort lokale UI-Update
    debouncedChange(newTextbelege); // Debounced Parent-Update
  }, [localTextbelege, debouncedChange]);

  return (
    <>
      <Typography variant="body2" color="textSecondary" gutterBottom>
        <strong>Textbelege ({localTextbelege.length}):</strong>
      </Typography>
      {localTextbelege.map((beleg: any, belegIndex: number) => (
        <Box key={belegIndex} sx={{ mb: 1, p: 1, backgroundColor: 'background.paper', borderRadius: 1, border: '1px solid #e0e0e0' }}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
            <Typography variant="caption" color="textSecondary">
              <strong>Textbeleg {belegIndex + 1}</strong>
            </Typography>
            {canEdit && (
              <Tooltip title="Textbeleg löschen">
                <IconButton
                  size="small"
                  onClick={() => handleDelete(belegIndex)}
                  sx={{ color: 'error.main' }}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Box>

          <FieldWithModificationIndicator
            isModified={isFieldModified ? isFieldModified(anforderungNummer, belegIndex, 'quellenangabe') : false}
            label="Quelle"
          >
            <LocalTextbelegeField
              label="Quelle"
              initialValue={beleg.quellenangabe || ''}
              belegIndex={belegIndex}
              field="quellenangabe"
              disabled={!canEdit}
              onFieldChange={handleFieldChange}
            />
          </FieldWithModificationIndicator>

          <FieldWithModificationIndicator
            isModified={isFieldModified ? isFieldModified(anforderungNummer, belegIndex, 'textauszug') : false}
            label="Textauszug"
          >
            <LocalTextbelegeField
              label="Textauszug"
              initialValue={beleg.textauszug || ''}
              belegIndex={belegIndex}
              field="textauszug"
              disabled={!canEdit}
              multiline
              rows={3}
              onFieldChange={handleFieldChange}
            />
          </FieldWithModificationIndicator>

          <FieldWithModificationIndicator
            isModified={isFieldModified ? isFieldModified(anforderungNummer, belegIndex, 'relevanz') : false}
            label="Relevanz"
          >
            <LocalTextbelegeField
              label="Relevanz"
              initialValue={beleg.relevanz || ''}
              belegIndex={belegIndex}
              field="relevanz"
              disabled={!canEdit}
              onFieldChange={handleFieldChange}
            />
          </FieldWithModificationIndicator>
        </Box>
      ))}

      {canEdit && (
        <Typography
          onClick={handleAdd}
          sx={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px', fontSize: '1rem', my: 1 }}
        >
          <AddIcon /> Textbeleg hinzufügen
        </Typography>
      )}
    </>
  );
});

// Lokaler TextField mit eigenem State (keine Parent-Re-Renders) - mit Label im Border wie LocalTextbelegeField
const LocalTextField = React.memo<{
  label: string;
  initialValue: string;
  anforderungNummer: number;
  field: string;
  disabled: boolean;
  multiline?: boolean;
  rows?: number;
  sx?: any;
  onLocalChange: (anforderungNummer: number, field: string, value: string) => void;
}>(({ label, initialValue, anforderungNummer, field, disabled, multiline = false, rows = 1, sx = {}, onLocalChange }) => {
  const [localValue, setLocalValue] = useState(initialValue);

  // Update lokaler Wert wenn sich initialValue ändert (z.B. nach Reset)
  useEffect(() => {
    setLocalValue(initialValue);
  }, [initialValue]);

  // Debounced Change für Parent-Update (optional für Live-Vorschau)
  const debouncedChange = useCallback(
    debounce((value: string) => {
      onLocalChange(anforderungNummer, field, value);
    }, 300), // 300ms Verzögerung wie bei LocalTextbelegeField
    [anforderungNummer, field, onLocalChange]
  );

  const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setLocalValue(newValue); // Sofortiges lokales Update
    debouncedChange(newValue); // Verzögertes Parent-Update
  }, [debouncedChange]);

  return (
    <TextField
      fullWidth
      multiline={multiline}
      {...(multiline ? { minRows: rows } : {})}
      label={label}
      value={localValue}
      disabled={disabled}
      onChange={handleChange}
      sx={{
        mb: 1,
        ...sx,
        '& .MuiInputBase-input': { overflow: 'hidden', resize: multiline ? 'vertical' : 'none' },
        '& .MuiInputBase-inputMultiline': { overflow: 'hidden', resize: multiline ? 'vertical' : 'none' }
      }}
      inputProps={{ style: { fontSize: '0.875rem' } }}
    />
  );
});

// Optimiertes Textbelege-Feld mit lokalem State
const LocalTextbelegeField = React.memo<{
  label: string;
  initialValue: string;
  belegIndex: number;
  field: string;
  disabled: boolean;
  multiline?: boolean;
  rows?: number;
  sx?: any;
  onFieldChange: (belegIndex: number, field: string, value: string) => void;
}>(({ label, initialValue, belegIndex, field, disabled, multiline = false, rows = 1, sx = {}, onFieldChange }) => {
  const [localValue, setLocalValue] = useState(initialValue);

  // Update lokaler Wert wenn sich initialValue ändert
  useEffect(() => {
    setLocalValue(initialValue);
  }, [initialValue]);

  // Debounced Change für Parent-Update
  const debouncedChange = useCallback(
    debounce((value: string) => {
      onFieldChange(belegIndex, field, value);
    }, 300), // 300ms für Textbelege
    [belegIndex, field, onFieldChange]
  );

  const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setLocalValue(newValue); // Sofortiges lokales Update
    debouncedChange(newValue); // Verzögertes Parent-Update
  }, [debouncedChange]);

  return (
    <TextField
      fullWidth
      multiline={multiline}
      {...(multiline ? { minRows: rows } : {})}
      label={label}
      value={localValue}
      disabled={disabled}
      onChange={handleChange}
      sx={{
        mb: 1,
        ...sx,
        '& .MuiInputBase-input': { overflow: 'hidden', resize: multiline ? 'vertical' : 'none' },
        '& .MuiInputBase-inputMultiline': { overflow: 'hidden', resize: multiline ? 'vertical' : 'none' }
      }}
      inputProps={{ style: { fontSize: '0.875rem' } }}
    />
  );
});

// Feld mit Markierung für geänderte Felder - basiert auf MUI-Beispiel-Struktur
const FieldWithModificationIndicator = React.memo<{
  children: React.ReactNode;
  isModified: boolean;
  label: string;
}>(({ children, isModified, label }) => {
  return (
    <Box
      className={isModified ? 'modified-field-container' : ''}
      sx={{
        // Styles für ALLE Felder - Orange wenn geändert, Dunkelgrau für normale Felder
        "& .MuiOutlinedInput-root": {
          backgroundColor: isModified ? "#fff8f0" : "white",
          "& .MuiOutlinedInput-notchedOutline": {
            borderColor: isModified ? "#ff6b35" : "#424242",
            borderWidth: isModified ? "2px" : "1px",
          },
          "&:hover": {
            "& .MuiOutlinedInput-notchedOutline": {
              borderColor: isModified ? "#e55a2b" : "#666666",
            },
          },
          "&.Mui-focused": {
            "& .MuiOutlinedInput-notchedOutline": {
              borderColor: isModified ? "#ff6b35" : "#424242",
              borderWidth: isModified ? "2px" : "2px",
            },
          },
          "& .MuiInputLabel-outlined": {
            color: isModified ? "#ff6b35" : "#424242",
            fontWeight: isModified ? "600" : "400",
            backgroundColor: isModified ? "#fff8f0" : "white",
            paddingLeft: "6px",
            paddingRight: "6px",
            "&.Mui-focused": {
              color: isModified ? "#ff6b35" : "#424242",
              fontWeight: isModified ? "600" : "500",
              backgroundColor: isModified ? "#fff8f0" : "white",
            },
          },
        },
        // Fallback für andere Label-Varianten
        "& .MuiInputLabel-root": {
          color: isModified ? "#ff6b35" : "#424242",
          fontWeight: isModified ? "600" : "400",
          backgroundColor: isModified ? "#fff8f0" : "white",
          paddingLeft: "6px",
          paddingRight: "6px",
          "&.Mui-focused": {
            color: isModified ? "#ff6b35" : "#424242",
            backgroundColor: isModified ? "#fff8f0" : "white",
          },
        },
      }}
    >
      {children}
    </Box>
  );
});

// Lokaler Select mit eigenem State (keine Parent-Re-Renders)
const LocalSelect = React.memo<{
  label: string;
  initialValue: string;
  anforderungNummer: number;
  field: string;
  disabled: boolean;
  options: { value: string; label: string }[];
  sx?: any;
  onLocalChange: (anforderungNummer: number, field: string, value: string) => void;
}>(({ label, initialValue, anforderungNummer, field, disabled, options, sx = {}, onLocalChange }) => {
  const [localValue, setLocalValue] = useState(initialValue);

  // Update lokaler Wert wenn sich initialValue ändert (z.B. nach Reset)
  useEffect(() => {
    setLocalValue(initialValue);
  }, [initialValue]);

  const handleChange = useCallback((e: any) => {
    const newValue = e.target.value;
    setLocalValue(newValue); // Sofortiges lokales Update
    onLocalChange(anforderungNummer, field, newValue); // Sofortiges Parent-Update für Selects
  }, [anforderungNummer, field, onLocalChange]);

  return (
    <>
      <Typography variant="body2" color="textSecondary" gutterBottom>
        <strong>{label}:</strong>
      </Typography>
      <FormControl size="small" sx={{ minWidth: 120, mb: 2, ...sx }}>
        <InputLabel>{label}</InputLabel>
        <Select
          value={localValue}
          label={label}
          disabled={disabled}
          onChange={handleChange}
        >
          {options.map(option => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </>
  );
});

const QSAnalysisDetail: React.FC = () => {
  const { analysisId } = useParams<{ analysisId: string }>();
  const navigate = useNavigate();
  const { role, isLoading: roleLoading } = useUserRole();

  const [analysis, setAnalysis] = useState<QSAnalysisDetailType | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [selectedAnforderung, setSelectedAnforderung] = useState<number | null>(null);
  const [changes, setChanges] = useState<QSChangeRequest[]>([]);
  const [notes, setNotes] = useState('');
  // Bestätigungsdialog für "Alle Änderungen zurücksetzen"
  const [confirmResetOpen, setConfirmResetOpen] = useState(false);

  // Lokaler State für Textbelege-Änderungen (Performance-Optimierung)
  const [textbelegeChanges, setTextbelegeChanges] = useState<{ [key: string]: any[] }>({});

  // State für Anforderung-spezifische Notizen
  const [anforderungNotes, setAnforderungNotes] = useState<{ [key: string]: string }>({});

  // State für QS-Prüfungsmarkierungen (welche Anforderungen wurden geprüft)
  const [reviewedAnforderungen, setReviewedAnforderungen] = useState<Set<number>>(new Set());

  // Callback für Textbelege-Änderungen
  const handleTextbelegeChange = useCallback((anforderungNummer: number, textbelege: any[]) => {
    setTextbelegeChanges(prev => ({
      ...prev,
      [anforderungNummer]: textbelege
    }));
  }, []);

  // Handler für lokale Änderungen (optional für Live-Vorschau)
  const handleLocalFieldChange = useCallback((anforderungNummer: number, field: string, value: string) => {
    const change: QSChangeRequest = {
      anforderungNummer,
      field,
      newValue: value
    };
    setChanges(prev => [...prev.filter(c =>
      !(c.anforderungNummer === anforderungNummer && c.field === field)
    ), change]);
  }, []);

  // Handler für allgemeine Notizen
  const handleNotesChange = useCallback((value: string) => {
    setNotes(value);
  }, []);

  // Handler für Anforderung-spezifische Notizen
  const handleAnforderungNotesChange = useCallback((anforderungNummer: number, value: string) => {
    setAnforderungNotes(prev => ({
      ...prev,
      [anforderungNummer]: value
    }));
  }, []);

  // Handler für QS-Prüfungsmarkierungen
  const handleReviewedToggle = useCallback((anforderungNummer: number) => {
    setReviewedAnforderungen(prev => {
      const newSet = new Set(prev);
      if (newSet.has(anforderungNummer)) {
        newSet.delete(anforderungNummer);
      } else {
        newSet.add(anforderungNummer);
      }
      return newSet;
    });
  }, []);

  // Hilfsfunktion um die richtige Datenquelle zu verwenden (QS-Results wenn vorhanden, sonst Original)
  const getSourceAnforderungen = () => {
    // Wenn QS-Results existieren, verwende diese als Basis
    if (analysis?.qsResults?.anforderungen) {
      return analysis.qsResults.anforderungen;
    }
    // Sonst verwende die originalen Results
    return analysis?.results?.anforderungen || [];
  };

  // Hilfsfunktion um IMMER die ursprünglichen KI-generierten Daten zu bekommen
  const getOriginalAnforderungen = () => {
    return analysis?.results?.anforderungen || [];
  };

  // Hilfsfunktion um ALLE Anforderungen zu bekommen (Merge von QS-Results und Original)
  const getAllAnforderungen = () => {
    const originalAnforderungen: QSAnforderung[] = getOriginalAnforderungen();
    const qsAnforderungen: QSAnforderung[] = analysis?.qsResults?.anforderungen || [];

    // Erstelle ein Map der QS-Anforderungen für schnelle Lookup
    const qsMap = new Map<number, QSAnforderung>();
    qsAnforderungen.forEach((qsAnf: QSAnforderung) => {
      qsMap.set(qsAnf.nummer, qsAnf);
    });

    // Für jede Original-Anforderung: verwende QS-Version wenn vorhanden, sonst Original
    return originalAnforderungen.map((originalAnf: QSAnforderung) => {
      const qsAnforderung = qsMap.get(originalAnf.nummer);
      return qsAnforderung || originalAnf;
    });
  };

  // Funktion um alle Anforderungen im korrekten Format zu erstellen
  const getAllAnforderungenWithChanges = useCallback(() => {
    const sourceAnforderungen: QSAnforderung[] = getSourceAnforderungen();
    if (sourceAnforderungen.length === 0) return [] as QSAnforderung[];

    return sourceAnforderungen.map((anforderung: QSAnforderung) => {
      const anforderungNummer = anforderung.nummer;

      // Basis-Anforderung mit allen ursprünglichen Feldern
      const updatedAnforderung: QSAnforderung = {
        ...anforderung,
        // Überschreibe mit Änderungen aus changes
        beschreibung: getQSAnforderungValue(anforderungNummer, 'beschreibung', anforderung.beschreibung),
        abdeckung: getQSAnforderungValue(anforderungNummer, 'abdeckung', anforderung.abdeckung),
        selbstbewertung: getQSAnforderungValue(anforderungNummer, 'selbstbewertung', anforderung.selbstbewertung),
        erweiterung: getQSAnforderungValue(anforderungNummer, 'erweiterung', anforderung.erweiterung),
        Sicherheitsskala: getQSAnforderungValue(anforderungNummer, 'Sicherheitsskala', anforderung.Sicherheitsskala),
        // Textbelege aus textbelegeChanges
        textbelege: textbelegeChanges[anforderungNummer] || anforderung.textbelege
      };

      return updatedAnforderung;
    });
  }, [getSourceAnforderungen, changes, textbelegeChanges]);

  // Funktion um alle aktuellen Werte zu sammeln (beim Speichern)
  const collectAllCurrentValues = useCallback(() => {
    const sourceAnforderungen = getSourceAnforderungen();
    if (sourceAnforderungen.length === 0) return [];

    // Hier würden wir die aktuellen Werte aus den lokalen Components sammeln
    // Für jetzt verwenden wir die existing changes + textbelege
    return getAllAnforderungenWithChanges();
  }, [getSourceAnforderungen, getAllAnforderungenWithChanges]);



  // Klappbares Vertragstext-Panel
  const [contractTextExpanded, setContractTextExpanded] = useState(true);

  // HTML-Tabellen in Markdown konvertieren
  const convertHtmlTablesToMarkdown = (text: string): string => {
    return text.replace(
      /<table>\s*([\s\S]*?)\s*<\/table>/gi,
      (match, tableContent) => {
        // Zeilen extrahieren
        const rows = tableContent.match(/<tr[^>]*>([\s\S]*?)<\/tr>/gi) || [];
        let markdownTable = '\n';

        rows.forEach((row: string, index: number) => {
          // Zellen extrahieren
          const cells = row.match(/<t[dh][^>]*>([\s\S]*?)<\/t[dh]>/gi) || [];
          const cellContent = cells.map((cell: string) => {
            const content = cell.replace(/<t[dh][^>]*>([\s\S]*?)<\/t[dh]>/i, '$1');
            return content.trim();
          });

          markdownTable += '| ' + cellContent.join(' | ') + ' |\n';

          // Header-Trenner nach der ersten Zeile
          if (index === 0) {
            markdownTable += '| ' + cellContent.map(() => '---').join(' | ') + ' |\n';
          }
        });

        return markdownTable;
      }
    );
  };

  // Vertragstext mit konvertierten HTML-Tabellen und Zeilenumbrüchen nach >
  const processedContractText = analysis?.contractText ?
    convertHtmlTablesToMarkdown(analysis.contractText).replace(/>/g, '>\n') : '';

  // Vertragstext in neuem Tab öffnen
  const openContractTextInNewTab = () => {
    if (analysis?.contractText) {
      const newWindow = window.open('', '_blank');
      if (newWindow) {
        // Markdown zu HTML konvertieren (gleiche Logik wie ReactMarkdown)
        const markdownToHtml = (markdown: string): string => {
          let html = markdown;

          // Tabellen verarbeiten (muss vor anderen Ersetzungen passieren)
          html = html.replace(
            /(\|[^\n]*\|[\r\n]+)+/g,
            (tableMatch) => {
              const lines = tableMatch.trim().split('\n').filter(line => line.trim() !== '');
              if (lines.length < 2) return tableMatch;

              let tableHtml = '<table>\n<thead>\n<tr>\n';

              // Header-Zeile
              const headerCells = lines[0].split('|').filter(cell => cell.trim() !== '');
              headerCells.forEach(cell => {
                tableHtml += `<th>${cell.trim()}</th>\n`;
              });
              tableHtml += '</tr>\n</thead>\n<tbody>\n';

              // Daten-Zeilen (überspringe die Trennlinie mit ---)
              for (let i = 1; i < lines.length; i++) {
                const line = lines[i];
                // Überspringe Trennlinien
                if (line.includes('---') || line.match(/^\|[\s\-:|]*\|$/)) continue;

                const cells = line.split('|').filter(cell => cell.trim() !== '');
                if (cells.length > 0) {
                  tableHtml += '<tr>\n';
                  cells.forEach(cell => {
                    tableHtml += `<td>${cell.trim()}</td>\n`;
                  });
                  tableHtml += '</tr>\n';
                }
              }

              tableHtml += '</tbody>\n</table>';
              return tableHtml;
            }
          );

          // Überschriften
          html = html
            .replace(/##### (.*$)/gim, '<h5>$1</h5>')
            .replace(/^#### (.*$)/gim, '<h4>$1</h4>')
            .replace(/^### (.*$)/gim, '<h3>$1</h3>')
            .replace(/^## (.*$)/gim, '<h2>$1</h2>')
            .replace(/^# (.*$)/gim, '<h1>$1</h1>');

          // Fett und Kursiv
          html = html
            .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
            .replace(/\*(.*?)\*/g, '<em>$1</em>');

          // Listen
          html = html
            .replace(/^\* (.*$)/gim, '<li>$1</li>')
            .replace(/^- (.*$)/gim, '<li>$1</li>')
            .replace(/^(\d+)\. (.*$)/gim, '<li>$2</li>');

          // Code-Blöcke
          html = html
            .replace(/```([\s\S]*?)```/g, '<pre><code>$1</code></pre>')
            .replace(/`([^`]+)`/g, '<code>$1</code>');

          // Links
          html = html.replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2">$1</a>');

          // Bilder
          html = html.replace(/!\[([^\]]*)\]\(([^)]+)\)/g, '<figure><img src="$2" alt="$1" /><figcaption>$1</figcaption></figure>');

          // Zeilenumbrüche und Absätze
          html = html
            .replace(/\n\n/g, '</p><p>')
          //.replace(/\n/g, '<br>');

          // KEINE Hervorhebung von XML-Tags mehr!
          // Die folgenden Zeilen wurden entfernt:
          // .replace(/(<\/?(?!table|tr|td|th|thead|tbody)[A-Za-z][A-Za-z0-9]*>)/g, ...)
          // .replace(/(&lt;\/?(?!table|tr|td|th|thead|tbody)[^&]*&gt;)/g, ...)

          return html;
        };

        const htmlContent = markdownToHtml(processedContractText);

        newWindow.document.write(`
          <!DOCTYPE html>
          <html>
          <head>
            <title>Vertragstext - ${analysis.contractPackage.name}</title>
            <meta charset="UTF-8">
            <style>
              body { 
                font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, 'Noto Sans', sans-serif; 
                margin: 0;
                padding: 20px;
                line-height: 1.6; 
                color: #333;
                background-color: #f5f5f5;
              }
              .container {
                max-width: 1200px;
                margin: 0 auto;
                background: white;
                padding: 30px;
                border-radius: 10px;
                box-shadow: 0 4px 6px rgba(0,0,0,0.1);
              }
              .document-title {
                background-color: #0F1F44;
                color: white;
                padding: 30px;
                border-radius: 10px;
                margin-bottom: 30px;
                text-align: center;
                box-shadow: 0 4px 6px rgba(0,0,0,0.1);
              }
              .document-title h1 {
                margin: 0;
                font-size: 2.2em;
                color: white;
              }
              .markdown-content {
                font-size: 16px;
                line-height: 1.7;
              }
              .markdown-content h1, .markdown-content h2, .markdown-content h3, 
              .markdown-content h4, .markdown-content h5, .markdown-content h6 { 
                margin-top: 30px; 
                margin-bottom: 15px; 
                font-weight: bold; 
                color: #2c3e50;
              }
              .markdown-content h1 { font-size: 2.2em; border-bottom: 2px solid #3498db; padding-bottom: 10px; }
              .markdown-content h2 { font-size: 1.8em; border-bottom: 1px solid #bdc3c7; padding-bottom: 8px; }
              .markdown-content h3 { font-size: 1.4em; }
              .markdown-content p { margin-bottom: 15px; }
              .markdown-content ul, .markdown-content ol { margin-bottom: 15px; padding-left: 25px; }
              .markdown-content li { margin-bottom: 8px; }
              .markdown-content strong { font-weight: bold; color: #2c3e50; }
              .markdown-content em { font-style: italic; }
              .markdown-content code { 
                background-color: #f8f9fa; 
                padding: 2px 6px; 
                border-radius: 3px; 
                font-family: 'Courier New', monospace;
                border: 1px solid #e9ecef;
                color: #e83e8c;
              }
              .markdown-content pre { 
                background-color: #f8f9fa; 
                padding: 15px; 
                border-radius: 5px; 
                overflow: auto; 
                fontSize: 14px;
                border: 1px solid #e9ecef;
                border-left: 4px solid #007bff;
              }
              .markdown-content pre code {
                background: none;
                padding: 0;
                border: none;
                color: #333;
              }
              .markdown-content table { 
                border-collapse: collapse; 
                width: 100%; 
                margin-bottom: 20px; 
                border: 1px solid #dee2e6;
                box-shadow: 0 2px 4px rgba(0,0,0,0.1);
              }
              .markdown-content th, .markdown-content td { 
                border: 1px solid #dee2e6; 
                padding: 12px; 
                text-align: left; 
                vertical-align: top;
              }
              .markdown-content th { 
                background-color: #f8f9fa; 
                fontWeight: bold;
                color: #495057;
                border-bottom: 2px solid #dee2e6;
              }
              .markdown-content tr:nth-of-type(even) { background-color: #f8f9fa; }
              .markdown-content tr:hover { background-color: #e9ecef; }
              .markdown-content a { color: #007bff; text-decoration: none; }
              .markdown-content a:hover { text-decoration: underline; }
              .markdown-content img { 
                max-width: 100%; 
                height: auto; 
                border-radius: 8px;
                box-shadow: 0 4px 8px rgba(0,0,0,0.1);
                margin: 20px 0;
                display: block;
                margin-left: auto;
                margin-right: auto;
              }
              .markdown-content figure {
                margin: 20px 0;
                text-align: center;
              }
              .markdown-content figcaption {
                font-style: italic;
                color: #666;
                margin-top: 8px;
                font-size: 0.9em;
              }
              .markdown-content svg {
                max-width: 100%;
                height: auto;
                border-radius: 8px;
                box-shadow: 0 4px 8px rgba(0,0,0,0.1);
                margin: 20px 0;
                display: block;
                margin-left: auto;
                margin-right: auto;
              }
            </style>
          </head>
          <body>
            <div class="container">
              <div class="document-title">
                <h1>Vertragstext: ${analysis.contractPackage.name}</h1>
              </div>
              <div class="markdown-content">
                ${htmlContent}
              </div>
            </div>
          </body>
          </html>
        `);
        newWindow.document.close();
      }
    }
  };

  useEffect(() => {
    if (roleLoading || !analysisId) return;

    if (role !== 'qs' && role !== 'super_qs' && role !== 'admin') {
      setError('Keine Berechtigung für QS-Bereich');
      setLoading(false);
      return;
    }

    fetchAnalysisDetail();
  }, [analysisId, role, roleLoading]);



  const fetchAnalysisDetail = async () => {
    if (!analysisId) return;

    try {
      setLoading(true);
      const data = await QSAnalysisService.getAnalysisDetail(analysisId);
      setAnalysis(data);
      setNotes(data.qsNotes || '');

      // Lade anforderungsspezifische Notizen und Prüfungsmarkierungen aus QS-Results
      if (data.qsResults?.anforderungen) {
        const notesFromQS: { [key: string]: string } = {};
        const reviewedFromQS = new Set<number>();

        data.qsResults.anforderungen.forEach((anf: any) => {
          if (anf.qs_notes) {
            notesFromQS[anf.nummer.toString()] = anf.qs_notes;
          }
          if (anf.qs_reviewed) {
            reviewedFromQS.add(anf.nummer);
          }
        });

        setAnforderungNotes(notesFromQS);
        setReviewedAnforderungen(reviewedFromQS);
      }
    } catch (err) {
      setError('Fehler beim Laden der Analyse');
      console.error('Error fetching analysis detail:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleStartReview = async () => {
    if (!analysisId) return;

    try {
      setSaving(true);
      await QSAnalysisService.startQSReview(analysisId);

      // Lokales Update des Status
      if (analysis) {
        const updatedAnalysis = {
          ...analysis,
          status: 'qs_in_review'
        };
        setAnalysis(updatedAnalysis);
      }

      setError(null);
    } catch (err) {
      setError('Fehler beim Starten der QS-Prüfung');
      console.error('Error starting QS review:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleSaveChanges = async () => {
    if (!analysisId) return;

    // Prüfe ob es Änderungen gibt (changes, textbelegeChanges, Notizen oder Prüfungsmarkierungen)
    const hasChanges = changes.length > 0 || Object.keys(textbelegeChanges).length > 0 ||
      notes.trim() !== '' || Object.keys(anforderungNotes).length > 0 ||
      reviewedAnforderungen.size > 0;
    if (!hasChanges) return;

    try {
      setSaving(true);

      // Erstelle alle Anforderungen mit Änderungen
      const allAnforderungen = getAllAnforderungenWithChanges();

      // Sende alle Anforderungen im korrekten Format + Notizen + Prüfungsmarkierungen
      await QSAnalysisService.saveQSChanges(analysisId, allAnforderungen, notes, anforderungNotes, Array.from(reviewedAnforderungen));

      // Lokales Update der Analysis mit den gespeicherten Änderungen
      if (analysis) {
        const updatedAnalysis = {
          ...analysis,
          qsResults: {
            ...analysis.qsResults,
            anforderungen: allAnforderungen,
            qs_notes: notes,
            qs_reviewed_at: new Date().toISOString()
          }
        };
        setAnalysis(updatedAnalysis);
      }

      // Reset States für unsaved changes
      setChanges([]);
      setTextbelegeChanges({});

      // Erfolgsmeldung anzeigen
      setError(null);
    } catch (err) {
      setError('Fehler beim Speichern der Änderungen');
      console.error('Error saving changes:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleSubmitReview = async (action: 'approve' | 'reject') => {
    if (!analysisId) return;

    try {
      setSaving(true);
      await QSAnalysisService.submitQSReview(analysisId, {
        action,
        notes,
        changes
      });
      navigate('/qs');
    } catch (err) {
      setError(`Fehler beim ${action === 'approve' ? 'Genehmigen' : 'Ablehnen'} der Analyse`);
      console.error('Error submitting review:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleSuperQSReview = async (action: 'approve' | 'reject') => {
    if (!analysisId) return;

    try {
      setSaving(true);
      await QSAnalysisService.submitSuperQSReview(analysisId, {
        action,
        notes
      });
      navigate('/qs');
    } catch (err) {
      setError(`Fehler beim ${action === 'approve' ? 'Genehmigen' : 'Ablehnen'} der SuperQS-Prüfung`);
      console.error('Error submitting super QS review:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleResetAnforderung = async (anforderungNummer: number) => {
    if (!analysisId) return;

    try {
      setSaving(true);

      // Backend: Gespeicherte QS-Results für diese Anforderung zurücksetzen
      await QSAnalysisService.resetAnforderungChanges(analysisId, anforderungNummer);

      // Frontend: Alle lokalen States für diese Anforderung zurücksetzen
      setChanges(prev => prev.filter(c => c.anforderungNummer !== anforderungNummer));
      setTextbelegeChanges(prev => {
        const updated = { ...prev };
        delete updated[anforderungNummer];
        return updated;
      });
      setAnforderungNotes(prev => {
        const updated = { ...prev };
        delete updated[anforderungNummer.toString()];
        return updated;
      });
      setReviewedAnforderungen(prev => {
        const newSet = new Set(prev);
        newSet.delete(anforderungNummer);
        return newSet;
      });

      // Granulares Update: Nur die spezifische QS-Anforderung aus analysis.qsResults entfernen
      if (analysis && analysis.qsResults?.anforderungen) {
        const updatedQSResults = {
          ...analysis.qsResults,
          anforderungen: analysis.qsResults.anforderungen.filter((anf: any) => anf.nummer !== anforderungNummer)
        };

        // Wenn keine QS-Anforderungen mehr übrig sind, entferne qsResults komplett
        const updatedAnalysis = {
          ...analysis,
          qsResults: updatedQSResults.anforderungen.length > 0 ? updatedQSResults : undefined
        };

        setAnalysis(updatedAnalysis);
      }

      setError(null);
    } catch (err) {
      setError('Fehler beim Zurücksetzen der Anforderung');
      console.error('Error resetting anforderung:', err);
    } finally {
      setSaving(false);
    }
  };

  // Alle Änderungen zurücksetzen (QS)
  const handleResetAllChanges = useCallback(async () => {
    if (!analysisId) return;

    try {
      setSaving(true);

      // Backend: Alle gespeicherten QS-Results löschen
      await QSAnalysisService.resetAllChanges(analysisId);

      // Frontend: Alle lokalen States zurücksetzen
      setChanges([]);
      setTextbelegeChanges({});
      setAnforderungNotes({});
      setNotes('');
      setReviewedAnforderungen(new Set());

      // Granulares Update: Nur qsResults aus analysis entfernen, ohne komplette Neuladen
      if (analysis) {
        const updatedAnalysis = {
          ...analysis,
          qsResults: undefined,
          qsNotes: undefined
        };
        setAnalysis(updatedAnalysis);
      }

      setError(null);
    } catch (err) {
      setError('Fehler beim Zurücksetzen aller Änderungen');
      console.error('Error resetting all changes:', err);
    } finally {
      setSaving(false);
    }
  }, [analysisId, analysis]);

  // Bestätigen und Reset ausführen
  const handleConfirmResetAll = useCallback(async () => {
    try {
      await handleResetAllChanges();
    } finally {
      setConfirmResetOpen(false);
    }
  }, [handleResetAllChanges]);

  // QS-Prüfung abschließen (für SuperQS Freigabe)
  const handleCompleteQSReview = async () => {
    if (!analysisId) return;

    try {
      setSaving(true);

      // Erst alle Änderungen speichern
      const allAnforderungen = collectAllCurrentValues();
      await QSAnalysisService.saveQSChanges(analysisId, allAnforderungen, notes, anforderungNotes, Array.from(reviewedAnforderungen));

      // Dann Status auf "qs_approved" setzen
      await QSAnalysisService.submitQSReview(analysisId, {
        action: 'approve',
        notes,
        changes: [],
        anforderungNotes,
        reviewedAnforderungen: Array.from(reviewedAnforderungen)
      });

      navigate('/qs');
    } catch (err) {
      setError('Fehler beim Abschließen der QS-Prüfung');
      console.error('Error completing QS review:', err);
    } finally {
      setSaving(false);
    }
  };

  // SuperQS: Direktes Freigeben ohne weitere Prüfung
  const handleDirectApproval = async () => {
    if (!analysisId) return;

    try {
      setSaving(true);
      await QSAnalysisService.submitSuperQSReview(analysisId, {
        action: 'approve',
        notes,
        anforderungNotes,
        reviewedAnforderungen: Array.from(reviewedAnforderungen)
      });
      navigate('/qs');
    } catch (err) {
      setError('Fehler beim direkten Freigeben');
      console.error('Error direct approval:', err);
    } finally {
      setSaving(false);
    }
  };



  // getStatusChip wurde entfernt - UniversalBadge wird jetzt verwendet

  // Berechtigungen basierend auf Rolle
  const isQS = role === 'qs';
  const isSuperQS = role === 'super_qs';
  const isAdmin = role === 'admin';

  // Erweiterte Bearbeitungslogik
  const canEdit = (analysis?.status === 'qs_in_review' ||
    analysis?.status === 'qs_rejected') &&
    (isQS || isSuperQS || isAdmin);
  const canSuperQSReview = analysis?.status === 'qs_completed';
  const canStartReview = analysis?.status === 'qs_pending';

  // Erweiterte Button-Sichtbarkeit basierend auf Status und Rolle
  const showQSButtons = () => {
    if (!canEdit) return false;

    // QS kann immer bearbeiten wenn canEdit true ist
    if (isQS) return true;

    // SuperQS und Admin können auch abgelehnte Analysen bearbeiten
    if ((isSuperQS || isAdmin) && (analysis?.status === 'qs_rejected')) {
      return true;
    }

    // Standard-Logik für in_review
    return (isSuperQS || isAdmin) && analysis?.status === 'qs_in_review';
  };

  const showSuperQSButtons = (isSuperQS || isAdmin) && (canSuperQSReview ||
    (canEdit && (analysis?.status === 'qs_rejected')));
  const showStartButton = (isQS || isSuperQS || isAdmin) && canStartReview;



  // Hilfsfunktion um QS-Anforderung Werte zu bekommen
  const getQSAnforderungValue = (anforderungNummer: number, field: string, originalValue: any) => {
    // 1) Bevorzugt lokale, noch nicht gespeicherte Änderungen
    const change = changes.find(c => c.anforderungNummer === anforderungNummer && c.field === field);
    if (change) {
      return change.newValue;
    }

    // 2) Falls vorhanden: bereits gespeicherte QS-Änderungen aus analysis.qsResults berücksichtigen
    const qsAnforderung = analysis?.qsResults?.anforderungen?.find((anf: any) => anf.nummer === anforderungNummer);
    if (qsAnforderung && (qsAnforderung as any)[field] !== undefined && (qsAnforderung as any)[field] !== null) {
      return (qsAnforderung as any)[field];
    }

    // 3) Fallback: ursprünglicher KI-Wert
    return originalValue;
  };

  // Prüft ob eine Anforderung überarbeitet wurde (visuelle Markierung)
  const isAnforderungModified = (anforderungNummer: number) => {
    // Prüfe aktuelle Field-Änderungen (noch nicht gespeichert)
    const hasFieldChanges = changes.some(c => c.anforderungNummer === anforderungNummer);

    // Prüfe aktuelle Textbelege-Änderungen (noch nicht gespeichert)
    const hasTextbelegeChanges = textbelegeChanges[anforderungNummer] !== undefined;

    // Prüfe aktuelle Anforderung-Notizen (noch nicht gespeichert)
    const hasCurrentNotes = anforderungNotes[anforderungNummer] && anforderungNotes[anforderungNummer].trim() !== '';

    // Prüfe ob bereits gespeicherte QS-Results existieren (bereits gespeicherte Änderungen)
    const qsAnforderung = analysis?.qsResults?.anforderungen?.find((anf: any) => anf.nummer === anforderungNummer);
    const hasSavedChanges = qsAnforderung && analysis?.results?.anforderungen;

    // Vergleiche QS-Anforderung mit Original um gespeicherte Änderungen zu erkennen
    let hasSavedModifications = false;
    if (hasSavedChanges) {
      const originalAnforderung = analysis.results.anforderungen.find((anf: any) => anf.nummer === anforderungNummer);
      if (originalAnforderung && qsAnforderung) {
        hasSavedModifications =
          qsAnforderung.beschreibung !== originalAnforderung.beschreibung ||
          qsAnforderung.abdeckung !== originalAnforderung.abdeckung ||
          qsAnforderung.selbstbewertung !== originalAnforderung.selbstbewertung ||
          qsAnforderung.erweiterung !== originalAnforderung.erweiterung ||
          qsAnforderung.Sicherheitsskala !== originalAnforderung.Sicherheitsskala ||
          JSON.stringify(qsAnforderung.textbelege) !== JSON.stringify(originalAnforderung.textbelege) ||
          (qsAnforderung as any).qs_notes; // Hat QS-Notizen
      }
    }

    return hasFieldChanges || hasTextbelegeChanges || hasCurrentNotes || hasSavedModifications;
  };

  // Prüft ob ein spezifisches Feld geändert wurde (für individuelle Feld-Markierung)
  const isFieldModified = (anforderungNummer: number, field: string) => {
    const originalAnforderung = analysis?.results?.anforderungen?.find((anf: any) => anf.nummer === anforderungNummer);
    const qsAnforderung = analysis?.qsResults?.anforderungen?.find((anf: any) => anf.nummer === anforderungNummer);

    if (!originalAnforderung) return false;

    // Ermittle den aktuellen Wert (mit aktuellen Änderungen)
    const currentValue = getQSAnforderungValue(anforderungNummer, field, (originalAnforderung as any)[field]);
    const originalValue = (originalAnforderung as any)[field];

    // Intelligente Änderungserkennung: Vergleiche aktuellen Wert mit Original
    // Für numerische Felder (abdeckung) vergleiche als Number
    if (field === 'abdeckung') {
      return Number(currentValue) !== Number(originalValue);
    }

    // Für String-Felder vergleiche als String (trim whitespace)
    const currentStr = String(currentValue || '').trim();
    const originalStr = String(originalValue || '').trim();

    return currentStr !== originalStr;
  };

  // Prüft ob Textbelege geändert wurden
  const isTextbelegeModified = (anforderungNummer: number) => {
    const originalAnforderung = analysis?.results?.anforderungen?.find((anf: any) => anf.nummer === anforderungNummer);

    if (!originalAnforderung) return false;

    // Ermittle die aktuellen Textbelege (mit aktuellen Änderungen)
    const currentTextbelege = getQSAnforderungTextbelege(anforderungNummer, originalAnforderung.textbelege || []);
    const originalTextbelege = originalAnforderung.textbelege || [];

    // Intelligente Änderungserkennung: Deep-Compare der Textbelege
    return JSON.stringify(currentTextbelege) !== JSON.stringify(originalTextbelege);
  };

  // Prüft ob Notizen geändert wurden
  const isNotesModified = (anforderungNummer: number) => {
    // Aktuelle Notizen (getrimmt)
    const currentNotes = (anforderungNotes[anforderungNummer] || '').trim();

    // Gespeicherte Notizen (getrimmt)
    const qsAnforderung = analysis?.qsResults?.anforderungen?.find((anf: any) => anf.nummer === anforderungNummer);
    const savedNotes = ((qsAnforderung as any)?.qs_notes || '').trim();

    // Original hatte keine Notizen, also jede Notiz ist eine Änderung
    // Intelligente Erkennung: Nur markieren wenn tatsächlich Inhalt vorhanden
    return currentNotes !== '' || savedNotes !== '';
  };

  // Prüft ob ein spezifisches Textbelege-Feld geändert wurde
  const isTextbelegFieldModified = (anforderungNummer: number, belegIndex: number, field: string) => {
    const originalAnforderung = analysis?.results?.anforderungen?.find((anf: any) => anf.nummer === anforderungNummer);

    if (!originalAnforderung || !originalAnforderung.textbelege || !originalAnforderung.textbelege[belegIndex]) {
      return false;
    }

    const originalValue = (originalAnforderung.textbelege[belegIndex] as any)[field] || '';

    // 1. Prüfe zuerst lokale Änderungen (textbelegeChanges)
    const key = `${anforderungNummer}`;
    const localTextbelege = textbelegeChanges[key];

    if (localTextbelege && localTextbelege[belegIndex] && localTextbelege[belegIndex][field] !== undefined) {
      // Es gibt lokale Änderungen - vergleiche mit Original
      const localValue = String(localTextbelege[belegIndex][field] || '').trim();
      const originalStr = String(originalValue || '').trim();
      return localValue !== originalStr;
    }

    // 2. Prüfe gespeicherte QS-Änderungen
    const qsAnforderung = analysis?.qsResults?.anforderungen?.find((anf: any) => anf.nummer === anforderungNummer);
    if (qsAnforderung && qsAnforderung.textbelege && qsAnforderung.textbelege[belegIndex]) {
      const qsValue = String((qsAnforderung.textbelege[belegIndex] as any)[field] || '').trim();
      const originalStr = String(originalValue || '').trim();
      return qsValue !== originalStr;
    }

    // 3. Keine Änderungen vorhanden
    return false;
  };

  const getQSAnforderungTextbelege = (anforderungNummer: number, originalTextbelege: any[]) => {
    const key = `${anforderungNummer}`;
    return textbelegeChanges[key] || originalTextbelege;
  };

  // Performante Textbelege-Wert-Funktion
  const getQSAnforderungTextbelegValue = (anforderungNummer: number, belegIndex: number, field: string, originalValue: string) => {
    const key = `${anforderungNummer}`;
    const textbelege = textbelegeChanges[key];
    if (textbelege && textbelege[belegIndex] && textbelege[belegIndex][field] !== undefined) {
      return textbelege[belegIndex][field];
    }
    return originalValue;
  };


  if (roleLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="100vh">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={3}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  if (loading || !analysis) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="100vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <SectionWrapper>
      <Box
        sx={{
          height: 'calc(100vh - 90px)',
          display: 'flex',
          flexDirection: 'column',
          p: 1,
          mt: 3
        }}
      >
        {/* Header - Fixed */}
        <Box sx={{ flexShrink: 0, mb: 2 }}>
          <Typography variant="h4" gutterBottom>
            QS-Analyse: {analysis.contractPackage.name}
          </Typography>
          <Box display="flex" gap={2} alignItems="center" mb={2}>
            <Typography variant="body1">
              <strong>Organisation:</strong> {analysis.organization.name}
            </Typography>
            <Typography variant="body1">
              <strong>Dienstleister:</strong> {analysis.provider.name}
            </Typography>
            <UniversalBadge
              type="qs_status"
              value={analysis.status}
              data-testid="qs-analysis-detail-status-badge"
            />
          </Box>




          {/* Notes */}
          <LocalNotesField
            label="Allgemeine QS-Notizen"
            initialValue={notes}
            onLocalChange={handleNotesChange}
            disabled={!canEdit && !canSuperQSReview}
            placeholder="Hier können Sie allgemeine Notizen zur Analyse eingeben..."
            inputProps={{ style: { fontSize: '1rem' } }}
          />
        </Box>

        {/* Main Content - Container für beide Spalten */}
        <Box sx={{
          flex: 1,
          minHeight: 0,
          mb: 2
        }}>
          <Grid container sx={{ height: '100%' }}>
            {/* Left: Contract Text */}
            <Grid item xs={12} md={contractTextExpanded ? 6 : 2} sx={{ height: '100%', pr: 1 }}>
              <Paper sx={{ height: '100%', overflow: 'auto', p: 2 }}>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                  <Typography variant="h6">
                    Vertragstext (Markdown) - {analysis.contractText?.length || 0} Zeichen
                  </Typography>
                  <Box display="flex" gap={1}>
                    <Tooltip title="Vertragstext ein-/ausklappen">
                      <IconButton
                        size="small"
                        onClick={() => setContractTextExpanded(!contractTextExpanded)}
                      >
                        {contractTextExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon2 />}
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Vertragstext in neuem Tab öffnen">
                      <IconButton
                        size="small"
                        onClick={openContractTextInNewTab}
                      >
                        <OpenInNewIcon />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Box>

                <Divider sx={{ mb: 2 }} />

                <Collapse in={contractTextExpanded}>

                  <Box sx={{
                    fontSize: '16px',
                    lineHeight: '1.7',
                    '& h1, & h2, & h3, & h4, & h5, & h6': {
                      marginTop: '30px',
                      marginBottom: '15px',
                      fontWeight: 'bold',
                      color: 'text.primary'
                    },
                    '& h1': {
                      fontSize: '2.2em',
                      borderBottom: '2px solid #3498db',
                      paddingBottom: '10px'
                    },
                    '& h2': {
                      fontSize: '1.8em',
                      borderBottom: '1px solid #bdc3c7',
                      paddingBottom: '8px'
                    },
                    '& h3': {
                      fontSize: '1.4em'
                    },
                    '& p': {
                      marginBottom: '15px'
                    },
                    '& ul, & ol': {
                      marginBottom: '15px',
                      paddingLeft: '25px'
                    },
                    '& li': {
                      marginBottom: '8px'
                    },
                    '& strong': {
                      fontWeight: 'bold',
                      color: 'text.primary'
                    },
                    '& em': {
                      fontStyle: 'italic'
                    },
                    '& code': {
                      backgroundColor: '#f8f9fa',
                      padding: '2px 6px',
                      borderRadius: '3px',
                      fontFamily: "'Courier New', monospace",
                      border: '1px solid #e9ecef',
                      color: '#e83e8c'
                    },
                    '& pre': {
                      backgroundColor: '#f8f9fa',
                      padding: '15px',
                      borderRadius: '5px',
                      overflow: 'auto',
                      fontSize: '14px',
                      border: '1px solid #e9ecef',
                      borderLeft: '4px solid #007bff'
                    },
                    '& pre code': {
                      background: 'none',
                      padding: 0,
                      border: 'none',
                      color: 'text.primary'
                    },
                    '& table': {
                      borderCollapse: 'collapse',
                      width: '100%',
                      marginBottom: '20px',
                      border: '1px solid #dee2e6',
                      boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                    },
                    '& th, & td': {
                      border: '1px solid #dee2e6',
                      padding: '12px',
                      textAlign: 'left',
                      verticalAlign: 'top'
                    },
                    '& th': {
                      backgroundColor: '#f8f9fa',
                      fontWeight: 'bold',
                      color: 'text.secondary',
                      borderBottom: '2px solid #dee2e6'
                    },
                    '& tr:nth-of-type(even)': {
                      backgroundColor: '#f8f9fa'
                    },
                    '& tr:hover': {
                      backgroundColor: '#e9ecef'
                    },
                    '& a': {
                      color: '#007bff',
                      textDecoration: 'none'
                    },
                    '& a:hover': {
                      textDecoration: 'underline'
                    }
                  }}>
                    {analysis.contractText ? (
                      <ReactMarkdown
                        remarkPlugins={[remarkGfm]}
                        rehypePlugins={[]}
                        components={{

                          h1: ({ children }) => <Typography variant="h4" component="h1">{children}</Typography>,
                          h2: ({ children }) => <Typography variant="h5" component="h2">{children}</Typography>,
                          h3: ({ children }) => <Typography variant="h6" component="h3">{children}</Typography>,
                          h4: ({ children }) => <Typography variant="subtitle1" component="h4">{children}</Typography>,
                          h5: ({ children }) => <Typography variant="subtitle2" component="h5">{children}</Typography>,
                          h6: ({ children }) => <Typography variant="body1" component="h6" sx={{ fontWeight: 'bold' }}>{children}</Typography>,
                          p: ({ children }) => <Typography variant="body2" component="p" sx={{ mb: 1 }}>{children}</Typography>,
                          strong: ({ children }) => <strong style={{ fontWeight: 'bold' }}>{children}</strong>,
                          em: ({ children }) => <em style={{ fontStyle: 'italic' }}>{children}</em>,
                          code: ({ children }) => <code style={{
                            backgroundColor: 'rgba(0,0,0,0.1)',
                            padding: '2px 4px',
                            borderRadius: 2,
                            fontFamily: 'monospace',
                            fontSize: '0.875rem'
                          }}>{children}</code>,
                          pre: ({ children }) => <pre style={{
                            backgroundColor: 'rgba(0,0,0,0.05)',
                            padding: 8,
                            borderRadius: 4,
                            overflow: 'auto',
                            fontSize: '0.875rem',
                            fontFamily: 'monospace'
                          }}>{children}</pre>,
                          ul: ({ children }) => <ul style={{ marginBottom: 8, paddingLeft: 16 }}>{children}</ul>,
                          ol: ({ children }) => <ol style={{ marginBottom: 8, paddingLeft: 16 }}>{children}</ol>,
                          li: ({ children }) => <li style={{ marginBottom: 4 }}>{children}</li>,
                          // Markdown-Tabellen (mit |)
                          table: ({ children }) => (
                            <Box sx={{ overflow: 'auto', mb: 2 }}>
                              <table style={{
                                borderCollapse: 'collapse',
                                width: '100%',
                                border: '1px solid #ddd'
                              }}>
                                {children}
                              </table>
                            </Box>
                          ),
                          thead: ({ children }) => <thead style={{ backgroundColor: '#f5f5f5' }}>{children}</thead>,
                          tbody: ({ children }) => <tbody>{children}</tbody>,
                          tr: ({ children }) => <tr>{children}</tr>,
                          th: ({ children }) => (
                            <th style={{
                              border: '1px solid #ddd',
                              padding: '8px',
                              textAlign: 'left',
                              fontWeight: 'bold',
                              backgroundColor: '#f0f0f0'
                            }}>
                              {children}
                            </th>
                          ),
                          td: ({ children }) => (
                            <td style={{
                              border: '1px solid #ddd',
                              padding: '8px',
                              textAlign: 'left'
                            }}>
                              {children}
                            </td>
                          ),

                          // Benutzerdefinierte Textverarbeitung für XML-Tags
                          text: ({ children }) => {
                            if (typeof children === 'string') {
                              return <HighlightedText text={children} />;
                            }
                            return <>{children}</>;
                          }
                        }}
                      >
                        {processedContractText}
                      </ReactMarkdown>
                    ) : (
                      <Typography variant="body2" color="textSecondary">
                        Kein Vertragstext verfügbar
                      </Typography>
                    )}
                  </Box>
                </Collapse>
              </Paper>
            </Grid>

            {/* Right: Anforderungen - Parallele Ansicht */}
            <Grid item xs={12} md={contractTextExpanded ? 6 : 10} sx={{ height: '100%' }}>
              <Paper sx={{ height: '100%', overflow: 'auto', p: 2 }}>
                <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                  <Typography variant="h6">
                    Anforderungen ({getAllAnforderungen().length})
                  </Typography>
                  {(isQS || isSuperQS || isAdmin) && (
                    <Chip
                      label={`${reviewedAnforderungen.size} von ${getAllAnforderungen().length} geprüft`}
                      color={reviewedAnforderungen.size === getAllAnforderungen().length ? 'success' : 'warning'}
                      size="small"
                      icon={<CheckCircleIcon />}
                      sx={{ backgroundColor: '#0F1F44' }}
                    />
                  )}
                </Box>
                <Divider sx={{ mb: 2 }} />

                {getAllAnforderungen().map((anforderung, index) => {
                  // Prüfe ob diese Anforderung überarbeitet wurde
                  const isModified = isAnforderungModified(anforderung.nummer);

                  // Finde die entsprechende Original-Anforderung
                  const originalAnforderung = getOriginalAnforderungen().find(orig => orig.nummer === anforderung.nummer);

                  // Fallback falls Original nicht gefunden wird (sollte nicht passieren)
                  if (!originalAnforderung) {
                    console.warn(`Original-Anforderung für Nummer ${anforderung.nummer} nicht gefunden`);
                    return null;
                  }

                  return (
                    <QSRequirementCard
                      key={anforderung.nummer}
                      anforderung={anforderung}
                      originalAnforderung={originalAnforderung}
                      isModified={isModified}
                      canEdit={canEdit}
                      isQS={isQS}
                      isSuperQS={isSuperQS}
                      isAdmin={isAdmin}
                      isReviewed={reviewedAnforderungen.has(anforderung.nummer)}
                      saving={saving}
                      getQSAnforderungValue={getQSAnforderungValue}
                      isFieldModified={isFieldModified}
                      isTextbelegFieldModified={isTextbelegFieldModified}
                      isNotesModified={isNotesModified}
                      anforderungNotes={anforderungNotes}
                      onLocalFieldChange={handleLocalFieldChange}
                      onTextbelegeChange={handleTextbelegeChange}
                      onAnforderungNotesChange={handleAnforderungNotesChange}
                      onReviewedToggle={handleReviewedToggle}
                      onResetAnforderung={handleResetAnforderung}
                    />
                  );
                })}
              </Paper>
            </Grid>
          </Grid>
        </Box>

        {/* Action Buttons - Fixed Footer */}
        {(showQSButtons() || showSuperQSButtons || showStartButton) && (
          <Box sx={{
            flexShrink: 0,
            px: 2,
            py: 1.5,
            backgroundColor: '#f5f5f5',
            borderRadius: 1,
            borderTop: '1px solid #e0e0e0',
            mt: 'auto'
          }}>
            {/* Linke Gruppe: Start/Save/Complete/SuperQS */}
            <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
              <Box display="flex" gap={2} flexWrap="wrap">
                {/* Start Review Button */}
                {showStartButton && (
                  <Button
                    variant="contained"
                    color="primary"
                    startIcon={<StartReviewIcon />}
                    onClick={handleStartReview}
                    disabled={saving}
                  >
                    QS-Prüfung starten
                  </Button>
                )}

                {/* QS Buttons (ohne Reset) */}
                {showQSButtons() && (
                  <>
                    <Button
                      variant="contained"
                      color="success"
                      startIcon={<SaveIcon />}
                      onClick={handleSaveChanges}
                      disabled={saving}
                    >
                      Aktuellen Stand speichern
                    </Button>

                    <Button
                      variant="contained"
                      color="info"
                      startIcon={<ApproveIcon />}
                      onClick={handleCompleteQSReview}
                      disabled={saving}
                    >
                      QS-Prüfung abschließen
                    </Button>
                  </>
                )}

                {/* SuperQS Buttons */}
                {showSuperQSButtons && (
                  <>
                    {canSuperQSReview && (
                      <>
                        <Button
                          variant="contained"
                          color="success"
                          startIcon={<ApproveIcon />}
                          onClick={handleDirectApproval}
                          disabled={saving}
                        >
                          Freigeben
                        </Button>

                        <Button
                          variant="outlined"
                          color="error"
                          startIcon={<RejectIcon />}
                          onClick={() => handleSuperQSReview('reject')}
                          disabled={saving}
                        >
                          Ablehnen
                        </Button>
                      </>
                    )}
                  </>
                )}
              </Box>

              {/* Rechte Gruppe: Reset ganz rechts separat */}
              {showQSButtons() && (
                <Box>
                  <Button
                    variant="outlined"
                    color="warning"
                    startIcon={<UndoIcon />}
                    onClick={() => setConfirmResetOpen(true)}
                    disabled={saving}
                  >
                    Alle Änderungen zurücksetzen
                  </Button>
                </Box>
              )}
            </Box>
          </Box>
        )}

        {/* Bestätigungsdialog: Alle Änderungen zurücksetzen */}
        <Dialog
          open={confirmResetOpen}
          onClose={() => setConfirmResetOpen(false)}
          maxWidth="sm"
          fullWidth
          PaperProps={{
            sx: { borderRadius: 2, boxShadow: '0 8px 32px rgba(0,0,0,0.12)' }
          }}
        >
          <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 2, borderBottom: '1px solid #e5e7eb' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <WarningIcon sx={{ color: '#f59e0b', fontSize: '1.5rem' }} />
              <Typography variant="h6" sx={{ fontWeight: 600, color: 'text.primary', fontSize: '1.125rem' }}>
                Alle Änderungen zurücksetzen
              </Typography>
            </Box>
            <IconButton onClick={() => setConfirmResetOpen(false)} size="small" sx={{ color: 'text.secondary' }}>
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent sx={{ py: 3 }}>
            <Typography variant="body1" sx={{ color: 'text.primary', mb: 1 }}>
              Möchten Sie wirklich alle QS-Änderungen zurücksetzen?
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              Dieser Vorgang setzt alle Felder, Textbelege, Notizen und Prüfungsmarkierungen auf den Originalzustand zurück. Dieser Schritt kann nicht rückgängig gemacht werden.
            </Typography>
          </DialogContent>
          <DialogActions sx={{ p: 2, borderTop: '1px solid #e5e7eb' }}>
            <Button onClick={() => setConfirmResetOpen(false)} variant="text">
              Abbrechen
            </Button>
            <Button onClick={handleConfirmResetAll} variant="contained" color="warning" startIcon={<UndoIcon />}>
              Zurücksetzen
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </SectionWrapper>
  );
};

export default QSAnalysisDetail; 